import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Input, Card, Stepper, Frame, Chip, IconChip,
} from '../../../components/onboarding/primitives';
import { paymentOrdersAPI } from '../../../services/api';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];

const DEFAULT_BANK_INFO = {
  bankName: 'البنك الأهلي السعودي',
  beneficiaryName: 'شركة LIS Techs',
  iban: 'SA00 0000 0000 0000 0000 0000',
};

/**
 * Props contract:
 *   state     — full onboarding state object.
 *   setState  — setter.
 *   goToStep  — (n: number) => void.
 *
 * API endpoints used (must exist in api.js):
 *   paymentOrdersAPI.getMyActive()   — GET  /payment-orders/my-active
 *     response.data shape (expected):
 *       { id, plan, branchCount, extraBranches, extraBranchTotal,
 *         subtotal, discount, discountCode, vatAmount, total, bankInfo? }
 *   paymentOrdersAPI.updateDraft({ discountCode }) — POST /payment-orders/draft
 *     200 -> same shape as getMyActive.
 *     400 -> { message: "رسالة عربية" }  (invalid/expired code)
 *   paymentOrdersAPI.submit()        — POST /payment-orders/submit
 *     200 -> { id, status: 'awaiting_receipt', ... }
 */
export default function Step6Checkout({ state, setState, goToStep, goBack }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);

  const [agree, setAgree] = useState(state.checkoutAgree || false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const bankInfo = order?.bankInfo || DEFAULT_BANK_INFO;

  // Load active draft on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await paymentOrdersAPI.getMyActive();
        if (cancelled) return;
        const data = res.data?.data || res.data || {};
        setOrder(data);
        setState((s) => ({ ...s, activeOrder: data }));
      } catch (e) {
        if (cancelled) return;
        const msg = e.response?.data?.message || 'تعذّر تحميل طلبك الحالي';
        setLoadError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCode = async () => {
    const c = (code || '').trim().toUpperCase();
    if (!c) return;
    setApplyingCode(true);
    setCodeError('');
    try {
      const res = await paymentOrdersAPI.updateDraft({ discountCode: c });
      const data = res.data?.data || res.data || {};
      setOrder(data);
      setState((s) => ({ ...s, activeOrder: data }));
      setCode('');
      toast.success('تم تطبيق الكود');
    } catch (e) {
      if (e.response?.status === 400) {
        setCodeError(e.response?.data?.message || 'كود غير صالح أو منتهٍ');
      } else {
        const msg = e.response?.data?.message || 'تعذّر تطبيق الكود';
        setCodeError(msg);
        toast.error(msg);
      }
    } finally {
      setApplyingCode(false);
    }
  };

  const removeCode = async () => {
    setApplyingCode(true);
    setCodeError('');
    try {
      const res = await paymentOrdersAPI.updateDraft({ discountCode: null });
      const data = res.data?.data || res.data || {};
      setOrder(data);
      setState((s) => ({ ...s, activeOrder: data }));
    } catch (e) {
      const msg = e.response?.data?.message || 'تعذّر إزالة الكود';
      toast.error(msg);
    } finally {
      setApplyingCode(false);
    }
  };

  const copyIban = () => {
    try {
      navigator.clipboard?.writeText(bankInfo.iban);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no-op */
    }
  };

  const submit = async () => {
    if (!agree || submitting || !order) return;
    setSubmitting(true);
    try {
      const res = await paymentOrdersAPI.submit();
      const data = res.data?.data || res.data || {};
      setState((s) => ({
        ...s,
        checkoutAgree: agree,
        activeOrder: { ...(s.activeOrder || order), ...data },
      }));
      // Free / trial plans (total === 0) are auto-approved on the backend
      // and bypass the bank-transfer / admin-approval phase. Skip Steps 7
      // (Receipt) and 8 (Pending) and land directly on the success screen.
      if (data.status === 'approved' || Number(data.total || 0) === 0) {
        goToStep(9);
      } else {
        goToStep(7);
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'تعذّر إرسال الطلب، حاول مرة أخرى';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render states ───────────────────────────────────
  if (loading) {
    return (
      <Frame
        maxWidth={1100}
        stepper={<Stepper steps={STEP_LABELS} current={5} />}
      >
        <div style={{ textAlign: 'center', padding: '80px 0', color: BRAND.muted }}>
          جاري تحميل تفاصيل الطلب…
        </div>
      </Frame>
    );
  }

  const onBack = () => { if (goBack) goBack(); else goToStep(5); };

  if (loadError || !order) {
    return (
      <Frame
        maxWidth={1100}
        stepper={<Stepper steps={STEP_LABELS} current={5} />}
        footer={
          <Button variant="outline" onClick={onBack} iconLeft={<Icon.Arrow dir="right" />}>
            السابق
          </Button>
        }
      >
        <Card padding={40} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>تعذّر تحميل طلبك</div>
          <div style={{ color: BRAND.muted, fontSize: 14 }}>{loadError || 'طلب غير متاح'}</div>
        </Card>
      </Frame>
    );
  }

  // Backend populates `planId` (the ObjectId ref) into a full document, and
  // also stores a frozen snapshot on the order itself (`planNameAr`,
  // `planSlug`, `planPrice`) so prices don't drift if the Plan doc changes.
  // Read from the populated doc first, fall back to the snapshot.
  const planDoc = order.planId && typeof order.planId === 'object' ? order.planId : null;
  const discount = Number(order.discountAmount ?? order.discount ?? 0);
  const extraBranches = Number(order.extraBranches || 0);
  const extraBranchTotal = Number(order.extraBranchTotal || 0);
  const extraDevices = Number(order.extraDevices || 0);
  const extraDeviceTotal = Number(order.extraDeviceTotal || 0);
  const extraDeviceUnitPrice = Number(order.extraDeviceUnitPrice || 0);
  const subtotal = Number(order.subtotal || 0);
  const vatAmount = Number(order.vatAmount || 0);
  const total = Number(order.total || 0);
  const appliedCode = order.discountCode || null;
  const planName = planDoc?.nameAr
    || planDoc?.name
    || order.planNameAr
    || order.planSlug
    || '—';
  // Trial / free plans skip the bank-transfer flow — surface this so the
  // bank info card is hidden, the CTA copy changes, and the explanatory
  // microcopy stops mentioning receipts.
  const isFreePlan = Number(order.total || 0) === 0;
  const planYearly = Number(
    planDoc?.yearlyPrice
    ?? order.planPrice
    ?? (subtotal - extraBranchTotal)
    ?? 0,
  );

  return (
    <Frame
      maxWidth={1100}
      stepper={<Stepper steps={STEP_LABELS} current={5} />}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onBack}
            iconLeft={<Icon.Arrow dir="right" />}
            disabled={submitting}
          >
            السابق
          </Button>
          <Button
            onClick={submit}
            disabled={!agree || submitting}
            iconLeft={<Icon.Bank size={16} />}
          >
            {submitting
              ? 'جاري الإرسال…'
              : isFreePlan ? 'تفعيل الباقة المجانية' : 'متابعة لرفع الإيصال'}
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5 }}>
          {isFreePlan ? 'تفعيل الباقة التجريبية' : 'مراجعة الطلب والدفع'}
        </h1>
        <p style={{ fontSize: 14.5, color: BRAND.muted, margin: 0 }}>
          {isFreePlan
            ? 'هذه باقة مجانية — التفعيل فوري بدون تحويل بنكي ولا انتظار للمصادقة.'
            : 'تأكد من التفاصيل قبل إرسال الطلب للمصادقة.'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 28, alignItems: 'flex-start' }}>
        {/* Form column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Discount */}
          <Card padding={24}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>
              كود خصم{' '}
              <span style={{ color: BRAND.muted, fontWeight: 400, fontSize: 12 }}>— اختياري</span>
            </div>
            {appliedCode ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: BRAND.mint,
                  padding: '10px 14px',
                  borderRadius: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                  <Icon.Check color={BRAND.greenDark} size={16} />
                  <span style={{ fontWeight: 600, color: BRAND.greenDark }}>كود {appliedCode}</span>
                  {discount > 0 && (
                    <span style={{ color: BRAND.greenDark }}>
                      — وفّرت {discount.toLocaleString()} ر.س
                    </span>
                  )}
                </div>
                <button
                  onClick={removeCode}
                  disabled={applyingCode}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: applyingCode ? 'not-allowed' : 'pointer',
                    color: BRAND.greenDark,
                    padding: 4,
                    display: 'flex',
                    opacity: applyingCode ? 0.5 : 1,
                  }}
                >
                  <Icon.X />
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      value={code}
                      onChange={(v) => {
                        setCode((v || '').toUpperCase());
                        setCodeError('');
                      }}
                      placeholder="LAUNCH20"
                      ltr
                      error={codeError}
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={applyCode}
                    disabled={applyingCode || !code.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {applyingCode ? '…' : 'تطبيق'}
                  </Button>
                </div>
                {codeError && (
                  <div
                    role="alert"
                    style={{
                      fontSize: 12,
                      color: BRAND.error,
                      marginTop: 8,
                      background: 'rgba(226,75,74,0.06)',
                      border: `1px solid rgba(226,75,74,0.2)`,
                      borderRadius: 8,
                      padding: '8px 10px',
                    }}
                  >
                    {codeError}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Payment method — hidden for free plans (no transfer needed) */}
          {!isFreePlan && (
          <Card padding={24}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>طريقة الدفع</div>
            <div
              style={{
                border: `1.5px solid ${BRAND.green}`,
                borderRadius: 14,
                padding: 18,
                background: '#fff',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  border: `2px solid ${BRAND.green}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                <div style={{ width: 10, height: 10, borderRadius: 5, background: BRAND.green }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <IconChip size={32}>
                    <Icon.Bank size={16} />
                  </IconChip>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>تحويل بنكي</div>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: BRAND.muted,
                    marginBottom: 16,
                    lineHeight: 1.6,
                  }}
                >
                  حوّل المبلغ إلى حساب الشركة ثم ارفع الإيصال للمصادقة.
                </div>

                <div
                  style={{
                    background: BRAND.mint,
                    borderRadius: 12,
                    padding: 18,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <AccountLine label="اسم البنك" value={bankInfo.bankName} />
                  <AccountLine label="اسم المستفيد" value={bankInfo.beneficiaryName} />
                  <AccountLine
                    label="IBAN"
                    value={bankInfo.iban}
                    mono
                    action={
                      <button
                        onClick={copyIban}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          background: '#fff',
                          border: `1px solid ${BRAND.border}`,
                          borderRadius: 8,
                          padding: '4px 10px',
                          fontSize: 11.5,
                          color: BRAND.greenDark,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                        }}
                      >
                        {copied ? <>✓ نُسخ</> : <><Icon.Copy /> نسخ</>}
                      </button>
                    }
                  />
                  <div style={{ height: 1, background: 'rgba(15,110,86,0.15)', margin: '4px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, color: BRAND.greenDark, fontWeight: 500 }}>
                      المبلغ المطلوب
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: BRAND.greenDark,
                        letterSpacing: -0.3,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {total.toLocaleString()} ر.س
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: BRAND.greenDark,
                      opacity: 0.8,
                      lineHeight: 1.5,
                    }}
                  >
                    حوّل المبلغ بالضبط (بلا تقريب) لتسريع المصادقة.
                  </div>
                </div>
              </div>
            </div>
          </Card>
          )}

          {/* Agree */}
          <Card padding={24}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: BRAND.green,
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <div>
                <div style={{ fontSize: 14, color: BRAND.text, lineHeight: 1.6, fontWeight: 500 }}>
                  أقرّ بأن البيانات المُدخلة صحيحة وأوافق على شروط الاشتراك.
                </div>
                <div style={{ fontSize: 12, color: BRAND.muted, marginTop: 4 }}>
                  {isFreePlan
                    ? 'يمكنك الترقية لباقة مدفوعة في أي وقت من إعدادات الاشتراك.'
                    : 'رسوم الاشتراك غير مستردّة بعد التفعيل.'}
                </div>
              </div>
            </label>
          </Card>
        </div>

        {/* Summary column (sticky) */}
        <div style={{ position: 'sticky', top: 20 }}>
          <OrderSummary
            planName={planName}
            planYearly={planYearly}
            extraBranches={extraBranches}
            extraBranchTotal={extraBranchTotal}
            extraDevices={extraDevices}
            extraDeviceTotal={extraDeviceTotal}
            extraDeviceUnitPrice={extraDeviceUnitPrice}
            subtotal={subtotal}
            discount={discount}
            appliedCode={appliedCode}
            vatAmount={vatAmount}
            total={total}
          />
        </div>
      </div>
    </Frame>
  );
}

// ─── AccountLine ───────────────────────────────────────
function AccountLine({ label, value, mono, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ fontSize: 12.5, color: BRAND.greenDark, opacity: 0.8 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: BRAND.greenDark,
            fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
            direction: mono ? 'ltr' : undefined,
            letterSpacing: mono ? 0.3 : 0,
          }}
        >
          {value}
        </div>
        {action}
      </div>
    </div>
  );
}

// ─── OrderSummary ──────────────────────────────────────
function OrderSummary({
  planName,
  planYearly,
  extraBranches,
  extraBranchTotal,
  extraDevices,
  extraDeviceTotal,
  extraDeviceUnitPrice,
  subtotal,
  discount,
  appliedCode,
  vatAmount,
  total,
}) {
  return (
    <Card padding={24} elevated>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>ملخّص طلبك</div>

      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 4,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>الباقة {planName}</div>
            <div style={{ fontSize: 12, color: BRAND.muted, marginTop: 2 }}>
              <Chip tone="mint" style={{ padding: '2px 8px', fontSize: 11 }}>سنوي</Chip>
            </div>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {planYearly.toLocaleString()} ر.س
          </div>
        </div>
      </div>

      <div style={{ borderTop: `1px dashed ${BRAND.border}`, margin: '12px 0' }} />

      <div style={{ fontSize: 13, marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: BRAND.muted,
            marginBottom: 6,
          }}
        >
          <span>الفرع الرئيسي</span>
          <span>مضمّن</span>
        </div>
        {extraBranches > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>فرع إضافي × {extraBranches}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {extraBranchTotal.toLocaleString()} ر.س
            </span>
          </div>
        )}
        {extraDevices > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span>
              جهاز إضافي × {extraDevices}
              {extraDeviceUnitPrice > 0 && (
                <span style={{ color: BRAND.muted, fontSize: 11.5, marginInlineStart: 6 }}>
                  ({extraDeviceUnitPrice.toLocaleString()} ر.س/جهاز)
                </span>
              )}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {extraDeviceTotal.toLocaleString()} ر.س
            </span>
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${BRAND.border}`, margin: '14px 0', paddingTop: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13.5,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          <span>الإجمالي الفرعي</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{subtotal.toLocaleString()} ر.س</span>
        </div>
        {discount > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              color: BRAND.greenDark,
              marginBottom: 8,
            }}
          >
            <span>خصم{appliedCode ? ` (${appliedCode})` : ''}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              −{discount.toLocaleString()} ر.س
            </span>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            color: BRAND.muted,
            marginBottom: 8,
          }}
        >
          <span>ضريبة القيمة المضافة 15%</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {vatAmount.toLocaleString()} ر.س
          </span>
        </div>
      </div>

      <div style={{ background: BRAND.mint, borderRadius: 14, padding: 18, marginTop: 8 }}>
        <div style={{ fontSize: 12, color: BRAND.greenDark, opacity: 0.85, marginBottom: 4 }}>
          الإجمالي النهائي
        </div>
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: BRAND.greenDark,
            letterSpacing: -0.6,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {total.toLocaleString()} <span style={{ fontSize: 15, fontWeight: 500 }}>ر.س</span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: BRAND.muted,
          marginTop: 14,
        }}
      >
        <Icon.Check color={BRAND.green} size={14} />
        <span>تفعيل فوري بعد مصادقة التحويل</span>
      </div>
    </Card>
  );
}
